import React, { useState } from 'react';
import { FaLock, FaCalendarAlt, FaUser, FaUsers, FaPaperPlane, FaBell, FaCheckCircle } from 'react-icons/fa';

const UnauthorizedDisclosureForm = ({ setStep, onSubmit }) => {
  const [formData, setFormData] = useState({
    dateDiscovered: '',
    timeDiscovered: '',
    dateOccurred: '',
    timeOccurred: '',
    discoveredBy: '',
    patientsAffected: '',
    patientNames: '',
    phiTypes: [],
    disclosureMethod: '',
    recipientInfo: '',
    incidentDescription: '',
    causeOfIncident: '',
    immediateActions: '',
    patientNotified: false,
    patientNotificationDate: '',
    patientNotificationMethod: '',
    ocrReportRequired: false,
    riskLevel: 'medium',
    preventiveMeasures: '',
    reportedBy: '',
    reportedByTitle: '',
    reportedByEmail: '',
    priority: 'medium'
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const phiTypeOptions = [
    'Patient Names',
    'Social Security Numbers',
    'Medical Record Numbers',
    'Health Insurance Information',
    'Medical History',
    'Treatment Information',
    'Prescription Information',
    'Lab Results',
    'Contact Information',
    'Billing Information'
  ];

  const disclosureMethodOptions = [
    'Verbal Communication',
    'Email',
    'Fax',
    'Mail',
    'Phone',
    'Text Message',
    'Physical Documents',
    'Electronic System',
    'Inadvertent Display',
    'Other'
  ];

  const riskLevels = [
    { value: 'low', label: 'Low - Minimal risk of harm', color: 'bg-green-100 text-green-800' },
    { value: 'medium', label: 'Medium - Some risk of harm', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'high', label: 'High - Significant risk of harm', color: 'bg-orange-100 text-orange-800' },
    { value: 'critical', label: 'Critical - Immediate risk of harm', color: 'bg-red-100 text-red-800' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Auto-determine OCR reporting requirement
    if (name === 'patientsAffected') {
      const count = parseInt(value) || 0;
      setFormData(prev => ({
        ...prev,
        [name]: value,
        ocrReportRequired: count >= 500
      }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleMultiSelectChange = (e, fieldName) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [fieldName]: checked 
        ? [...prev[fieldName], value]
        : prev[fieldName].filter(item => item !== value)
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.dateDiscovered) newErrors.dateDiscovered = 'Date discovered is required';
    if (!formData.discoveredBy.trim()) newErrors.discoveredBy = 'Discovered by is required';
    if (!formData.patientsAffected) newErrors.patientsAffected = 'Number of patients affected is required';
    if (!formData.incidentDescription.trim()) newErrors.incidentDescription = 'Incident description is required';
    if (!formData.reportedBy.trim()) newErrors.reportedBy = 'Reporter name is required';
    if (!formData.reportedByEmail.trim()) newErrors.reportedByEmail = 'Reporter email is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      await onSubmit({
        ...formData,
        formType: 'HIPAADisclosure',
        subType: 'UnauthorizedDisclosure',
        submittedAt: new Date(),
        severity: formData.riskLevel
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (risk) => {
    const riskObj = riskLevels.find(r => r.value === risk);
    return riskObj ? riskObj.color : 'bg-yellow-100 text-yellow-800';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="inline-flex size-16 items-center justify-center rounded-full bg-violet-100">
            <FaLock className="size-8 text-violet-600" aria-hidden="true" />
          </div>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Unauthorized Disclosure Report
          </h1>
          <p className="mt-2 text-base text-gray-600">
            Report accidental or intentional disclosure of PHI to unauthorized persons
          </p>
          <div className={`mt-4 inline-flex items-center rounded-md px-3 py-1 text-sm font-medium ${getRiskColor(formData.riskLevel)}`}>
            Risk Level: {formData.riskLevel.charAt(0).toUpperCase() + formData.riskLevel.slice(1)}
          </div>
        </div>

        {/* Form Card */}
        <div className="overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-gray-900/5">
          <form onSubmit={handleSubmit}>
            <div className="space-y-12 px-6 py-8 sm:px-10 sm:py-10">
              {/* Incident Details */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaCalendarAlt className="size-5 text-gray-400" />
                  Incident Details
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  When and how the disclosure was discovered
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="dateDiscovered" className="block text-sm/6 font-medium text-gray-900">
                      Date Discovered <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="dateDiscovered"
                        name="dateDiscovered"
                        type="date"
                        value={formData.dateDiscovered}
                        onChange={handleInputChange}
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.dateDiscovered ? 'outline-red-500' : ''}`}
                      />
                      {errors.dateDiscovered && <p className="mt-1 text-sm text-red-600">{errors.dateDiscovered}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="timeDiscovered" className="block text-sm/6 font-medium text-gray-900">
                      Time Discovered
                    </label>
                    <div className="mt-2">
                      <input
                        id="timeDiscovered"
                        name="timeDiscovered"
                        type="time"
                        value={formData.timeDiscovered}
                        onChange={handleInputChange}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="dateOccurred" className="block text-sm/6 font-medium text-gray-900">
                      Date Occurred (if different)
                    </label>
                    <div className="mt-2">
                      <input
                        id="dateOccurred"
                        name="dateOccurred"
                        type="date"
                        value={formData.dateOccurred}
                        onChange={handleInputChange}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="timeOccurred" className="block text-sm/6 font-medium text-gray-900">
                      Time Occurred
                    </label>
                    <div className="mt-2">
                      <input
                        id="timeOccurred"
                        name="timeOccurred"
                        type="time"
                        value={formData.timeOccurred}
                        onChange={handleInputChange}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="discoveredBy" className="block text-sm/6 font-medium text-gray-900">
                      Discovered By <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="discoveredBy"
                        name="discoveredBy"
                        type="text"
                        value={formData.discoveredBy}
                        onChange={handleInputChange}
                        placeholder="Name and title of person who discovered the disclosure"
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.discoveredBy ? 'outline-red-500' : ''}`}
                      />
                      {errors.discoveredBy && <p className="mt-1 text-sm text-red-600">{errors.discoveredBy}</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Affected Patients */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaUsers className="size-5 text-gray-400" />
                  Affected Patients
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Information about affected patients
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="patientsAffected" className="block text-sm/6 font-medium text-gray-900">
                      Number of Patients Affected <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="patientsAffected"
                        name="patientsAffected"
                        type="number"
                        value={formData.patientsAffected}
                        onChange={handleInputChange}
                        placeholder="Enter number"
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.patientsAffected ? 'outline-red-500' : ''}`}
                      />
                      {errors.patientsAffected && <p className="mt-1 text-sm text-red-600">{errors.patientsAffected}</p>}
                      {formData.ocrReportRequired && (
                        <p className="mt-2 text-sm font-medium text-orange-600">
                          ⚠️ OCR reporting required (500+ individuals affected)
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="riskLevel" className="block text-sm/6 font-medium text-gray-900">
                      Risk Assessment
                    </label>
                    <div className="mt-2">
                      <select
                        id="riskLevel"
                        name="riskLevel"
                        value={formData.riskLevel}
                        onChange={handleInputChange}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      >
                        {riskLevels.map(risk => (
                          <option key={risk.value} value={risk.value}>{risk.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="patientNames" className="block text-sm/6 font-medium text-gray-900">
                      Patient Names (if known and applicable)
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="patientNames"
                        name="patientNames"
                        rows={3}
                        value={formData.patientNames}
                        onChange={handleInputChange}
                        placeholder="List patient names if known (use initials if preferred for privacy)"
                        className="block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm/6 font-medium text-gray-900 mb-4">Types of PHI Disclosed</label>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {phiTypeOptions.map(phiType => (
                        <div key={phiType} className="flex gap-3">
                          <div className="flex h-6 shrink-0 items-center">
                            <div className="group grid size-4 grid-cols-1">
                              <input
                                type="checkbox"
                                value={phiType}
                                checked={formData.phiTypes.includes(phiType)}
                                onChange={(e) => handleMultiSelectChange(e, 'phiTypes')}
                                className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-violet-600 checked:bg-violet-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600"
                              />
                              <svg fill="none" viewBox="0 0 14 14" className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white">
                                <path d="M3 8L6 11L11 3.5" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="opacity-0 group-has-[:checked]:opacity-100" />
                              </svg>
                            </div>
                          </div>
                          <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">{phiType}</label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Disclosure Details */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaPaperPlane className="size-5 text-gray-400" />
                  Disclosure Details
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  How and to whom PHI was disclosed
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8">
                  <div>
                    <label htmlFor="disclosureMethod" className="block text-sm/6 font-medium text-gray-900">
                      Method of Disclosure
                    </label>
                    <div className="mt-2">
                      <select
                        id="disclosureMethod"
                        name="disclosureMethod"
                        value={formData.disclosureMethod}
                        onChange={handleInputChange}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      >
                        <option value="">Select method...</option>
                        {disclosureMethodOptions.map(method => (
                          <option key={method} value={method}>{method}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="recipientInfo" className="block text-sm/6 font-medium text-gray-900">
                      Recipient Information
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="recipientInfo"
                        name="recipientInfo"
                        rows={3}
                        value={formData.recipientInfo}
                        onChange={handleInputChange}
                        placeholder="Who received the PHI? Include names, organizations, contact information if known"
                        className="block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="incidentDescription" className="block text-sm/6 font-medium text-gray-900">
                      Detailed Description <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="incidentDescription"
                        name="incidentDescription"
                        rows={4}
                        value={formData.incidentDescription}
                        onChange={handleInputChange}
                        placeholder="Describe what happened, including circumstances and timeline"
                        className={`block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.incidentDescription ? 'outline-red-500' : ''}`}
                      />
                      {errors.incidentDescription && <p className="mt-1 text-sm text-red-600">{errors.incidentDescription}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="causeOfIncident" className="block text-sm/6 font-medium text-gray-900">
                      Cause of Disclosure
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="causeOfIncident"
                        name="causeOfIncident"
                        rows={3}
                        value={formData.causeOfIncident}
                        onChange={handleInputChange}
                        placeholder="What caused this disclosure? (human error, system failure, process breakdown, etc.)"
                        className="block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="immediateActions" className="block text-sm/6 font-medium text-gray-900">
                      Immediate Actions Taken
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="immediateActions"
                        name="immediateActions"
                        rows={3}
                        value={formData.immediateActions}
                        onChange={handleInputChange}
                        placeholder="Describe any immediate steps taken to address the disclosure"
                        className="block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Patient Notification */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaBell className="size-5 text-gray-400" />
                  Patient Notification
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Patient notification information
                </p>

                <div className="mt-10 space-y-6">
                  <div className="flex gap-3">
                    <div className="flex h-6 shrink-0 items-center">
                      <div className="group grid size-4 grid-cols-1">
                        <input
                          type="checkbox"
                          name="patientNotified"
                          checked={formData.patientNotified}
                          onChange={handleInputChange}
                          className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-violet-600 checked:bg-violet-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600"
                        />
                        <svg fill="none" viewBox="0 0 14 14" className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white">
                          <path d="M3 8L6 11L11 3.5" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="opacity-0 group-has-[:checked]:opacity-100" />
                        </svg>
                      </div>
                    </div>
                    <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">Patient(s) have been notified</label>
                  </div>

                  {formData.patientNotified && (
                    <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                      <div>
                        <label htmlFor="patientNotificationDate" className="block text-sm/6 font-medium text-gray-900">
                          Notification Date
                        </label>
                        <div className="mt-2">
                          <input
                            id="patientNotificationDate"
                            name="patientNotificationDate"
                            type="date"
                            value={formData.patientNotificationDate}
                            onChange={handleInputChange}
                            className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="patientNotificationMethod" className="block text-sm/6 font-medium text-gray-900">
                          Notification Method
                        </label>
                        <div className="mt-2">
                          <input
                            id="patientNotificationMethod"
                            name="patientNotificationMethod"
                            type="text"
                            value={formData.patientNotificationMethod}
                            onChange={handleInputChange}
                            placeholder="How were patients notified?"
                            className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <label htmlFor="preventiveMeasures" className="block text-sm/6 font-medium text-gray-900">
                      Preventive Measures
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="preventiveMeasures"
                        name="preventiveMeasures"
                        rows={3}
                        value={formData.preventiveMeasures}
                        onChange={handleInputChange}
                        placeholder="What measures will be taken to prevent similar disclosures?"
                        className="block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Reporter Information */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaUser className="size-5 text-gray-400" />
                  Reporter Information
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Your contact information
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="reportedBy" className="block text-sm/6 font-medium text-gray-900">
                      Your Name <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="reportedBy"
                        name="reportedBy"
                        type="text"
                        value={formData.reportedBy}
                        onChange={handleInputChange}
                        placeholder="Your full name"
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.reportedBy ? 'outline-red-500' : ''}`}
                      />
                      {errors.reportedBy && <p className="mt-1 text-sm text-red-600">{errors.reportedBy}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="reportedByTitle" className="block text-sm/6 font-medium text-gray-900">
                      Your Title
                    </label>
                    <div className="mt-2">
                      <input
                        id="reportedByTitle"
                        name="reportedByTitle"
                        type="text"
                        value={formData.reportedByTitle}
                        onChange={handleInputChange}
                        placeholder="Your job title"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="reportedByEmail" className="block text-sm/6 font-medium text-gray-900">
                      Your Email <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="reportedByEmail"
                        name="reportedByEmail"
                        type="email"
                        value={formData.reportedByEmail}
                        onChange={handleInputChange}
                        placeholder="your.email@example.com"
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.reportedByEmail ? 'outline-red-500' : ''}`}
                      />
                      {errors.reportedByEmail && <p className="mt-1 text-sm text-red-600">{errors.reportedByEmail}</p>}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-center gap-x-4 border-t border-gray-200 px-6 py-4 sm:px-10">
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-x-2 rounded-md bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-violet-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-0.5 size-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <FaCheckCircle aria-hidden="true" className="-ml-0.5 size-5" />
                    <span>Submit Report</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UnauthorizedDisclosureForm;
